import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sanitizeHtml } from "@/components/RichTextEditor";
import { Printer, ArrowLeft, Lock } from "lucide-react";

// Which document each print route pulls from
const MODULES = {
  "program-identity": {
    endpoint: "/program-identity",
    title: "Program Identity",
    subtitle: "The DNA of your football program",
  },
  "staff-manual": {
    endpoint: "/staff-manual",
    title: "Staff Manual",
    subtitle: "Everything coaches need — sign to acknowledge",
  },
  "player-handbook": {
    endpoint: "/player-handbook",
    title: "Player Handbook",
    subtitle: "Standards, expectations, discipline",
  },
};

export default function DocSectionsPrint() {
  const { kind } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  const mod = MODULES[kind];
  const canPrint = user && (user.role === "head_coach" || user.role === "athletic_director");

  useEffect(() => {
    if (!mod) { setErr("Unknown document."); return; }
    if (!canPrint) return; // guard
    (async () => {
      try {
        const { data } = await api.get(mod.endpoint);
        setItems(data);
      } catch (e) {
        setErr("Could not load document.");
      }
    })();
  }, [mod, canPrint]);

  useEffect(() => {
    if (items) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [items]);

  if (!mod) return <div className="p-8 text-red-400">Unknown document.</div>;
  if (!canPrint) {
    return (
      <div className="p-8 max-w-lg" data-testid="doc-print-forbidden">
        <div className="flex items-center gap-3 text-amber mb-3">
          <Lock className="w-5 h-5" /> <span className="font-display text-2xl uppercase">Head Coach / AD only</span>
        </div>
        <div className="text-sm text-slate-400">
          Only the head coach or athletic director can print program documents. Ask them to share a copy.
        </div>
      </div>
    );
  }
  if (err) return <div className="p-8 text-red-400">{err}</div>;
  if (!items) return <div className="p-8 text-slate-400">Loading…</div>;

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div data-testid="doc-print-page">
      {/* Print styles — hide app chrome, force white background */}
      <style>{`
        @media print {
          aside, [data-testid="topbar"], [data-testid="doc-print-toolbar"] { display: none !important; }
          body, html, .App { background: #fff !important; }
          .doc-print-root { padding: 0 !important; margin: 0 !important; color: #000 !important; }
          .doc-print-root a { color: #000 !important; text-decoration: none !important; }
          .doc-print-root .section { page-break-inside: avoid; }
          .doc-print-root .page-break { page-break-before: always; }
          .doc-print-root h1, .doc-print-root h2, .doc-print-root h3 { page-break-after: avoid; }
          .doc-print-root .prose-print h1 { color: #000 !important; }
          .doc-print-root .prose-print h2 { color: #000 !important; }
          .doc-print-root .prose-print h3 { color: #444 !important; }
          .doc-print-root .prose-print blockquote { border-left-color: #666 !important; color: #333 !important; }
        }
        @page { margin: 0.6in; }

        /* On-screen preview — black text on white so it matches print */
        .prose-print h1 {
          font-family: "Bebas Neue","Barlow Condensed",Impact,sans-serif;
          letter-spacing: 0.03em; font-size: 1.75rem; line-height: 1.1;
          text-transform: uppercase; margin: 1rem 0 0.5rem; color: #000;
        }
        .prose-print h2 {
          font-family: "Bebas Neue","Barlow Condensed",Impact,sans-serif;
          letter-spacing: 0.03em; font-size: 1.35rem; line-height: 1.15;
          text-transform: uppercase; margin: 0.75rem 0 0.4rem; color: #000;
        }
        .prose-print h3 {
          font-family: "Barlow Condensed","Bebas Neue",sans-serif;
          letter-spacing: 0.08em; font-size: 1rem; text-transform: uppercase;
          margin: 0.6rem 0 0.25rem; color: #444;
        }
        .prose-print p { margin: 0.35rem 0; }
        .prose-print strong { color: #000; font-weight: 700; }
        .prose-print ul { list-style: disc; padding-left: 1.4rem; margin: 0.4rem 0; }
        .prose-print ol { list-style: decimal; padding-left: 1.4rem; margin: 0.4rem 0; }
        .prose-print li { margin: 0.15rem 0; }
        .prose-print blockquote {
          border-left: 3px solid #666; padding: 0.1rem 0 0.1rem 0.75rem;
          margin: 0.5rem 0; color: #333; font-style: italic;
        }
        .prose-print hr { border: none; border-top: 1px solid #999; margin: 0.75rem 0; }
      `}</style>

      {/* On-screen toolbar (hidden when printing) */}
      <div className="bg-surface border-b border-surface-3 p-4 flex items-center gap-3 sticky top-0 z-10" data-testid="doc-print-toolbar">
        <button
          onClick={() => nav(-1)}
          data-testid="doc-print-back"
          className="flex items-center gap-1 text-xs font-condensed uppercase tracking-widest text-slate-400 hover:text-amber"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="flex-1" />
        <button
          onClick={() => window.print()}
          data-testid="doc-print-btn"
          className="flex items-center gap-2 bg-amber hover:bg-amber-600 text-[#0B0D12] px-4 py-2 text-[11px] font-condensed uppercase tracking-widest"
        >
          <Printer className="w-3 h-3" /> Print
        </button>
      </div>

      <div className="doc-print-root max-w-4xl mx-auto bg-white text-black p-10">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 border-black pb-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-gray-600">Gridiron OS · {user?.program_name || "Your Program"}</div>
            <div className="text-4xl font-bold uppercase mt-1">{mod.title}</div>
            <div className="text-sm text-gray-700 mt-1 italic">{mod.subtitle}</div>
          </div>
          <div className="text-right text-xs text-gray-600">
            <div>Printed: {today}</div>
            <div>{items.length} section{items.length !== 1 ? "s" : ""}</div>
            <div className="mt-1">Printed by: {user.name}</div>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-gray-500 italic py-8 text-center">
            This document has no sections yet.
          </div>
        ) : (
          <div className="space-y-6">
            {items.map((s, i) => (
              <div key={s.key || i} className="section" data-testid={`print-section-${s.key || i}`}>
                <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-1">
                  Section {i + 1}
                </div>
                <h2 className="text-2xl font-bold uppercase tracking-wide mb-2 pb-1 border-b border-gray-400">
                  {s.title}
                </h2>
                <SectionBody body={s.body} />
              </div>
            ))}
          </div>
        )}

        {/* Signature block — Staff Manual only. Break onto a fresh page. */}
        {kind === "staff-manual" && items.length > 0 && (
          <div className="page-break mt-10" data-testid="signature-block">
            <div className="border-t-2 border-black pt-6">
              <div className="text-[11px] uppercase tracking-[0.3em] text-gray-600">Signature Page</div>
              <div className="text-3xl font-bold uppercase mt-1">Coach Acknowledgement</div>
            </div>

            <div className="mt-6 text-sm leading-relaxed text-black">
              <p className="mb-4">
                I, the undersigned, acknowledge that I have received, read, and understood
                the <strong>{mod.title}</strong> for {user?.program_name || "our program"} for the upcoming season. I agree
                to uphold the standards, expectations, and procedures outlined within — including
                but not limited to coach responsibilities, communication standards, practice
                expectations, sideline procedures, and travel procedures.
              </p>
              <p className="mb-4">
                I understand that as a member of this staff I represent the program at all times
                and will conduct myself in a manner consistent with the culture, values, and
                integrity established by the head coach.
              </p>
              <p className="mb-8">
                Signing below confirms that I will follow this manual and hold my peers accountable
                to the same standard.
              </p>
            </div>

            <div className="mt-10 space-y-8">
              <SignatureRow label="Coach Name (Printed)" />
              <SignatureRow label="Position / Title" />
              <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8">
                <SignatureRow label="Coach Signature" />
                <SignatureRow label="Date" />
              </div>
            </div>

            <div className="mt-14 pt-6 border-t border-gray-300 text-xs text-gray-700 leading-relaxed">
              <div className="font-semibold text-black uppercase tracking-widest text-[11px] mb-2">Head Coach Confirmation</div>
              <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8 mt-4">
                <SignatureRow label="Head Coach Signature" />
                <SignatureRow label="Date" />
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 pt-4 border-t border-gray-300 text-[10px] text-gray-500 flex justify-between">
          <div>Confidential — Coaches & Program Staff Only</div>
          <div>gridiron-os.com</div>
        </div>
      </div>
    </div>
  );
}

function SectionBody({ body }) {
  const value = body || "";
  if (!value) {
    return <div className="text-gray-400 italic text-sm">(No content)</div>;
  }
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(value);
  if (!looksLikeHtml) {
    return (
      <div className="prose-print text-[13px] text-black whitespace-pre-wrap leading-relaxed">
        {value}
      </div>
    );
  }
  return (
    <div
      className="prose-print text-[13px] text-black leading-relaxed"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
    />
  );
}

function SignatureRow({ label }) {
  return (
    <div>
      <div className="border-b border-black h-8" />
      <div className="text-[10px] uppercase tracking-[0.25em] text-gray-600 mt-1">{label}</div>
    </div>
  );
}
